import React from 'react'
import { Box, Button, Dialog, Typography } from '@mui/material'
import { GOLD, GOLD_GLOW, formatTicket } from '../../../utils/luckyDraw'

// The overlay darkens first, then the cards lift off one by one.
const LIFT_DELAY_MS = 300
const FLIGHT_MS = 700
// Overshoots a little, so a card pops into its row rather than sliding to a stop.
const FLIGHT_EASING = 'cubic-bezier(0.34, 1.3, 0.64, 1)'
// Between cards, both ways. Also taken off each card's width, so the two must stay one value.
const GAP_EM = 1.1

/** The transform that lays a row's box exactly over its wall card, scaled from the top-left. */
const wallTransform = (from, to) => {
  const dx = from.left - to.left
  const dy = from.top - to.top
  const scaleX = from.width / to.width
  const scaleY = from.height / to.height

  return `translate(${dx}px, ${dy}px) scale(${scaleX}, ${scaleY})`
}

/**
 * Flies each row in from the lit wall card it names. Runs as the dialog enters, when the rows are
 * laid out but not yet painted, so each starts exactly over its card.
 */
const flyFromWall = (dialog, stepMs) => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

  // Every rect is read before any flight starts: a read after an animate() forces a style flush.
  const rows = [...dialog.querySelectorAll('[data-from]')]
  const measured = rows.map((row) => {
    const card = document.querySelector(`[data-ticket="${row.dataset.from}"]`)

    return { row, from: card?.getBoundingClientRect(), to: row.getBoundingClientRect() }
  })

  measured.forEach(({ row, from, to }, index) => {
    // No card when a failed draw refetch has swapped the wall for an error; the row fades in place.
    const start = from ? wallTransform(from, to) : 'none'
    // Hidden until its turn: a row parked visibly over its card sits undimmed above the overlay.
    const flight = [
      { transform: start, opacity: 0 },
      { transform: 'none', opacity: 1 },
    ]

    row.animate(flight, {
      duration: FLIGHT_MS,
      delay: LIFT_DELAY_MS + index * stepMs,
      easing: FLIGHT_EASING,
      fill: 'backwards',
    })
  })
}

/**
 * The winners of the press just finished, up to three cards a row. CONTINUE puts it away and gives
 * the wall back without starting the next draw; Escape does the same.
 */
const WinnerPopout = ({ winners, open, onClose }) => {
  const count = winners.length
  // Floored at one: an empty list renders nothing, but it still has to produce valid lengths.
  const columns = Math.max(Math.min(count, 3), 1)
  const rows = Math.max(Math.ceil(count / 3), 1)
  // Cards lift off one after another, compressed once the list is long enough that a flat step
  // would still be arriving seconds in.
  const stepMs = count > 1 ? Math.min(180, 1500 / (count - 1)) : 0

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      // Fires once per open, before paint, so a re-render of the page cannot restart the flight.
      TransitionProps={{ onEnter: (node) => flyFromWall(node, stepMs) }}
      // The page scrolls on <html>, not <body> — Page.js sets overflow-x there, which computes the
      // other axis to auto. MUI's lock hides body's overflow and pads body to replace a scrollbar
      // that was never body's, so the real one stays and the content shifts under it.
      disableScrollLock
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          // Safe: a list taller than the screen overflows downward, where it scrolls, instead of
          // pushing its top above the edge, where it cannot.
          justifyContent: 'safe center',
          gap: { xs: 2, md: 4 },
          p: { xs: 1, md: 3 },
          backgroundColor: 'rgba(7, 7, 13, 0.84)',
          // MUI's dark Paper lays an elevation tint over the colour above.
          backgroundImage: 'none',
        },
      }}
    >
      <Typography
        sx={{
          color: GOLD,
          textShadow: GOLD_GLOW,
          fontWeight: 700,
          letterSpacing: '0.3em',
          fontSize: 'clamp(0.75rem, 1.4vw, 1.4rem)',
          mb: { xs: 1, md: 3 },
        }}
      >
        CONGRATULATIONS
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          // A short last row sits in the middle rather than hanging off the left.
          justifyContent: 'center',
          width: '95%',
          // About 600px a card, so a lone winner is not a bar across the projection.
          maxWidth: columns * 600,
          // The height the list may take split between the rows, budgeted for two-line names, and
          // the width between the columns. The gap is in em, so spacing tracks the type.
          fontSize: `clamp(0.75rem, min(${(11 / rows).toFixed(2)}vh, ${(5 / columns).toFixed(2)}vw), 2rem)`,
          gap: `${GAP_EM}em`,
        }}
      >
        {winners.map((winner) => (
          <Typography
            key={winner.id}
            // The wall card this row flies in from.
            data-from={winner.ticket}
            sx={{
              // The wall's lit gold and bloom. Not its border or inset ring: at this size both read
              // as a white frame.
              background: `linear-gradient(180deg, #ffe89a 0%, ${GOLD} 100%)`,
              boxShadow: '0 0 24px 8px rgba(247,201,72,0.5)',
              color: '#101010',
              borderRadius: '0.2em',
              px: '1.25em',
              py: '0.6em',
              // No global box-sizing reset: without this the padding widens each card past its
              // share of the row, and only two fit.
              boxSizing: 'border-box',
              // A share of the row less one gap, so the gaps always fit and a fourth card never does.
              flex: `0 0 calc(${(100 / columns).toFixed(3)}% - ${GAP_EM}em)`,
              // Number over name, from the top, so numbers line up across a row whatever each
              // name's length.
              display: 'flex',
              flexDirection: 'column',
              gap: '0.35em',
              alignItems: 'center',
              // The flight's transform is measured from the top-left corner.
              transformOrigin: '0 0',
              fontSize: 'inherit',
              fontWeight: 800,
              lineHeight: 1.2,
              letterSpacing: '0.04em',
              textAlign: 'center',
              textTransform: 'uppercase',
              overflowWrap: 'anywhere',
            }}
          >
            <Box
              component='span'
              // Written as the hover peek writes it, so the room and the operator read one format.
              sx={{
                fontSize: '0.75em',
                fontWeight: 700,
                letterSpacing: '0.12em',
                color: 'rgba(16, 16, 16, 0.65)',
              }}
            >
              #{formatTicket(winner.ticket)}
            </Box>
            <Box
              component='span'
              // Fills the height under the number, so a short name centres beside a long one.
              sx={{ flex: 1, display: 'flex', alignItems: 'center' }}
            >
              {winner.companyName}
            </Box>
          </Typography>
        ))}
      </Box>

      <Button
        onClick={onClose}
        sx={{
          mt: { xs: 2, md: 4 },
          px: 7,
          py: 1.4,
          borderRadius: 999,
          border: '1px solid rgba(247, 201, 72, 0.45)',
          color: GOLD,
          fontWeight: 700,
          letterSpacing: '0.2em',
          fontSize: 'clamp(0.85rem, 1.3vw, 1.2rem)',
          '&:hover': { borderColor: GOLD, backgroundColor: 'rgba(247, 201, 72, 0.12)' },
        }}
      >
        CONTINUE
      </Button>
    </Dialog>
  )
}

export default WinnerPopout
